/**
 * Floating phrases above the Spark.
 *
 * Phrases dance: gentle curve trajectory, slight rotation, scale-overshoot
 * appearance. Each layer gets its own colour and rhythm so the eye reads
 * "+1" as info but "тёплое" or "я даю этому место" as a meaningful gesture.
 *
 * Animation profiles per tier:
 *   - numeric    — 1.6s, small rise + horizontal sway, gold
 *   - sensory    — 2.8s, longer curve, soft green
 *   - body       — 2.8s, gentle drift, cream
 *   - elemental  — 3.0s, bright drift, gold-bright
 *   - awareness  — 3.5s, large pulse + glow, cream with golden halo
 *   - special    — 5.5s, screen-centered, large, sacred glow, no horizontal sway
 *
 * Opacity envelope keeps phrases visible for ~85–90 % of their lifetime
 * so the eye can land on them.
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useMemo } from 'react';
import { useGameStore, type LiveFloatingPhrase } from '../stores/game.js';
import type { PhraseEntry } from '@sai-clicker/game-core';
import styles from './FloatingPhrasesLayer.module.css';

interface AnimationProfile {
  readonly durationSec: number;
  readonly riseDistancePx: number;
  /** Horizontal sway range — phrase drifts ± this amount as it rises. */
  readonly swayPx: number;
  /** Rotation range in degrees. */
  readonly rotateDeg: number;
  readonly initialScale: number;
  readonly peakScale: number; // overshoot
  readonly finalScale: number;
  readonly opacityTimes: readonly [number, number, number, number];
}

function profileFor(phrase: PhraseEntry): AnimationProfile {
  if (phrase.special !== undefined) {
    return {
      durationSec: 5.5,
      riseDistancePx: 40,
      swayPx: 0,
      rotateDeg: 0,
      initialScale: 0.6,
      peakScale: 1.1,
      finalScale: 1.0,
      opacityTimes: [0, 0.12, 0.85, 1],
    };
  }
  if (phrase.layer === 'awareness') {
    return {
      durationSec: 3.5,
      riseDistancePx: 80,
      swayPx: 8,
      rotateDeg: 2,
      initialScale: 0.55,
      peakScale: 1.15,
      finalScale: 1.0,
      opacityTimes: [0, 0.1, 0.85, 1],
    };
  }
  if (phrase.layer === 'numeric') {
    return {
      durationSec: 1.6,
      riseDistancePx: 60,
      swayPx: 18,
      rotateDeg: 6,
      initialScale: 0.7,
      peakScale: 1.1,
      finalScale: 0.95,
      opacityTimes: [0, 0.1, 0.82, 1],
    };
  }
  // sensory / body / elemental / program / creator
  return {
    durationSec: 2.8,
    riseDistancePx: 80,
    swayPx: 14,
    rotateDeg: 4,
    initialScale: 0.65,
    peakScale: 1.1,
    finalScale: 1.0,
    opacityTimes: [0, 0.08, 0.88, 1],
  };
}

/** Map a layer name to a CSS class for colour and weight.
 * `styles` is typed as Record<string, string | undefined> under our strict
 * tsconfig (noUncheckedIndexedAccess), so we coalesce to an empty string
 * to keep the return type clean. In practice every class exists in the CSS. */
function classForLayer(layer: PhraseEntry['layer']): string {
  const fallback = styles.word ?? '';
  switch (layer) {
    case 'numeric':
      return styles.numeric ?? fallback;
    case 'sensory':
      return styles.sensory ?? fallback;
    case 'body':
      return styles.body ?? fallback;
    case 'elemental':
      return styles.elemental ?? fallback;
    case 'awareness':
      return styles.awareness ?? fallback;
    case 'program':
      return styles.program ?? fallback;
    case 'creator':
      return styles.creator ?? fallback;
    default:
      return fallback;
  }
}

interface FloatingItemProps {
  readonly entry: LiveFloatingPhrase;
  readonly onDone: (key: number) => void;
}

/**
 * One floating phrase. Memoizes the per-spawn random offsets so re-renders
 * of the parent layer never reshuffle the trajectory mid-flight.
 */
function FloatingItem({ entry, onDone }: FloatingItemProps): JSX.Element {
  const isSpecial = entry.phrase.special !== undefined;
  const profile = useMemo(() => profileFor(entry.phrase), [entry.phrase]);

  // Random per-spawn sway direction so identical phrases don't move identically.
  // Seeded once (memo'd by key) — never reshuffles after mount.
  const { swayX, tiltDeg } = useMemo(() => {
    const sx = (Math.random() * 2 - 1) * profile.swayPx;
    const td = (Math.random() * 2 - 1) * profile.rotateDeg;
    return { swayX: sx, tiltDeg: td };
  }, [profile.swayPx, profile.rotateDeg]);

  const className = [
    styles.phrase,
    classForLayer(entry.phrase.layer),
    isSpecial ? styles.special : null,
  ]
    .filter(Boolean)
    .join(' ');

  // Special: centered horizontally, vertical from entry.originY.
  // Regular: positioned from entry.originX/originY (set by Spark in a ring around it).
  const positionStyle = isSpecial
    ? { left: '50%', top: `${entry.originY * 100}%` }
    : { left: `${entry.originX * 100}%`, top: `${entry.originY * 100}%` };

  return (
    <motion.span
      className={className}
      style={positionStyle}
      initial={{
        opacity: 0,
        x: 0,
        y: 0,
        scale: profile.initialScale,
        rotate: 0,
      }}
      animate={{
        opacity: [0, 1, 1, 0],
        x: [0, swayX * 0.6, swayX],
        y: [0, -profile.riseDistancePx * 0.5, -profile.riseDistancePx],
        scale: [profile.initialScale, profile.peakScale, profile.finalScale],
        rotate: [0, tiltDeg * 0.5, tiltDeg],
      }}
      exit={{ opacity: 0 }}
      transition={{
        duration: profile.durationSec,
        times: profile.opacityTimes as unknown as number[],
        ease: 'easeOut',
      }}
      onAnimationComplete={() => {
        onDone(entry.key);
      }}
    >
      {entry.phrase.text}
    </motion.span>
  );
}

export function FloatingPhrasesLayer(): JSX.Element {
  const liveFloatingPhrases = useGameStore((s) => s.liveFloatingPhrases);
  const removeFloatingPhrase = useGameStore((s) => s.removeFloatingPhrase);

  return (
    <div className={styles.layer} aria-hidden>
      <AnimatePresence>
        {liveFloatingPhrases.map((entry) => (
          <FloatingItem key={entry.key} entry={entry} onDone={removeFloatingPhrase} />
        ))}
      </AnimatePresence>
    </div>
  );
}
